import {
  Alert,
  App as AntApp,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Empty,
  Flex,
  Input,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CheckCircleOutlined,
  CloudServerOutlined,
  CustomerServiceOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  HistoryOutlined,
  KeyOutlined,
  LinkOutlined,
  MessageOutlined,
  ReadOutlined,
  ReloadOutlined,
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { ChannelServiceControlCard } from "../../components/ChannelServiceControlCard";
import { DataTable, useTableParams } from "../../components/DataTable";
import { api, type Paged } from "../../services/api";
import { useAuth } from "../../services/auth";
import { useCharacterScope } from "../../services/character";
import "./live.css";

interface CharacterRef {
  id: string;
  key: string;
  displayName: string;
}

interface Distribution {
  key: string;
  count: number;
}

interface SessionRow {
  id: string;
  clientSessionId: string;
  externalSessionId: string | null;
  mode: string;
  status: string;
  roomIntroduction: string | null;
  acknowledgedThroughSequence: number;
  roomGeneratedThroughSequence: number;
  startedAt: string;
  endedAt: string | null;
  effectiveEndedAt: string;
  lastRequestAt: string;
  durationBasis: "reported" | "inactivity_timeout" | "current";
  durationSeconds: number;
  metrics: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  endpointId: string;
  endpointKey: string;
  endpointTitle: string | null;
  roomName: string;
  accountId: string;
  accountName: string;
  providerKey: string;
  character: CharacterRef;
  runtimeNode: { id: string; nodeKey: string } | null;
  eventCount: number;
  encounterCount: number;
  eventTypeCounts: Record<string, number>;
  amountsByCurrency: Array<{ currency: string; amount: number }>;
  hasEndpointMemory: boolean;
  viewerPeak: number | null;
}

interface EventRow {
  id: string;
  sessionId: string | null;
  endpointId: string;
  endpointTitle: string;
  providerKey: string;
  character: CharacterRef;
  sourceSequence: number | null;
  localEventId: string | null;
  direction: "inbound" | "outbound" | "system";
  eventType: string;
  status: string;
  actorName: string | null;
  channelActorId: string | null;
  targetContact: { id: string; preferredName: string | null } | null;
  textPreview: string | null;
  executedTextPreview: string | null;
  text: string | null;
  executedText: string | null;
  replyKind: string | null;
  origin: string | null;
  runtimeTaskId: string | null;
  runtimeTaskRevision: number | null;
  sourceFromSequence: number | null;
  sourceThroughSequence: number | null;
  sourceEventCount: number;
  roomReplyCoveredAt: string | null;
  localSongId: string | null;
  songTitle: string | null;
  brainRunId: string | null;
  startedAt: string | null;
  generatedAt: string | null;
  executedAt: string | null;
  occurredAt: string;
  createdAt: string;
  updatedAt: string;
  amount: number | null;
  currency: string | null;
}

interface SongRequestRow {
  songKey: string;
  songTitle: string;
  totalCount: number;
  successCount: number;
  failedCount: number;
  pendingCount: number;
  duplicateCount: number;
  requesterCount: number;
  latestRequesterName: string;
  latestRequestedAt: string;
}

interface SongRequesterRow {
  requesterKey: string;
  channelActorId: string | null;
  requesterName: string;
  totalCount: number;
  successCount: number;
  failedCount: number;
  pendingCount: number;
  duplicateCount: number;
  latestRequestedAt: string;
}

interface LiveTestDataClearResult {
  sessions: number;
  events: number;
  contacts: number;
  actorLinks: number;
  encounters: number;
  endpointMemories: number;
  orphanActors: number;
}

interface ContactActor {
  linkId: string;
  linkMethod: string;
  confidence: number | null;
  status: string;
  linkedAt: string;
  verifiedAt: string | null;
  id: string;
  providerKey: string;
  identityNamespace: string;
  externalActorIdMasked: string | null;
  displayName: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
}

interface ContactRow {
  id: string;
  userId: string | null;
  isUserBound: boolean;
  testSessionId: string | null;
  scope: string;
  preferredName: string | null;
  status: string;
  firstEncounteredAt: string;
  lastEncounteredAt: string;
  meaningfulEncounterCount: number;
  memoryVersion: number;
  mergedIntoId: string | null;
  createdAt: string;
  updatedAt: string;
  character: CharacterRef;
  actorLinkCount: number;
  encounterCount: number;
  targetedEventCount: number;
  actors: ContactActor[];
}

interface BindingRow {
  id: string;
  userId: string;
  actorDisplayName: string | null;
  externalActorIdMasked: string | null;
  historicalActorKeyCount: number;
  status: string;
  expiresAt: string;
  submittedAt: string | null;
  confirmedAt: string | null;
  rejectedAt: string | null;
  revokedAt: string | null;
  effectiveFrom: string | null;
  createdAt: string;
  updatedAt: string;
  providerKey: string;
  accountId: string;
  accountName: string;
  character: CharacterRef;
  endpoint: { id: string; key: string; title: string } | null;
}

interface MemoryRow {
  id: string;
  kind: "endpoint" | "contact";
  sessionId: string;
  endpointId: string;
  endpointTitle: string;
  providerKey?: string;
  character: CharacterRef;
  summary: string;
  salience: number;
  topics: string[];
  sourceEventCount: number;
  sourceActionCount?: number;
  completedReplyCount?: number;
  contactId?: string;
  contactName?: string;
  isUserBound?: boolean;
  providers?: string[];
  brainRunId?: string | null;
  analysisVersion?: number;
  preferredNameEvidenceEventId?: string | null;
  startedAt: string;
  endedAt: string;
  createdAt: string;
}

interface NodeRecord {
  id: string;
  nodeKey: string;
  publicKeyFingerprint: string | null;
  status: string;
  capabilities: Record<string, unknown>;
  lastSeenAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  updatedAt: string;
  sessionCount: number;
}

interface RuntimeNodeAccessRecord {
  id: string;
  nodeKey: string;
  deviceName: string | null;
  fingerprint: string | null;
  status: string;
  hasCredential: boolean;
  lastSeenAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  updatedAt: string;
  sessionCount: number;
}

interface LiveRuntimePairingStatus {
  environment: "development" | "production";
  active: boolean;
  createdAt: string | null;
  expiresAt: string | null;
}

interface LiveRuntimePairingCodeResult {
  environment: "development" | "production";
  pairingCode: string;
  expiresAt: string;
}

interface EndpointRecord {
  id: string;
  key: string;
  title: string | null;
  displayName: string;
  externalEndpointIdMasked: string | null;
  enabled: boolean;
  configuredAt: string | null;
  lastEventAt: string | null;
  createdAt: string;
  updatedAt: string;
  account: {
    id: string;
    key: string;
    providerKey: string;
    displayName: string | null;
    status: string;
    capabilities: Record<string, unknown>;
    capabilitiesCheckedAt: string | null;
  };
  character: CharacterRef;
  sessionCount: number;
  eventCount: number;
  memoryCount: number;
}

interface LiveOverview {
  generatedAt: string;
  totals: {
    sessions: number;
    events: number;
    actors: number;
    contacts: number;
    bindings: number;
    endpointMemories: number;
    encounters: number;
    endpoints: number;
    nodes: number;
  };
  distributions: {
    sessionsByStatus: Distribution[];
    sessionsByMode: Distribution[];
    eventsByDirection: Distribution[];
    contactsByStatus: Distribution[];
    bindingsByStatus: Distribution[];
  };
  integrity: {
    memoryEligibleSessions: number;
    sessionsWithMemory: number;
    sessionsWithoutMemory: number;
    contactsWithEncounter: number;
    contactsBoundToUser: number;
  };
  recentSessions: SessionRow[];
  nodes: NodeRecord[];
  endpoints: EndpointRecord[];
}

const providerNames: Record<string, string> = {
  bilibili: "哔哩哔哩",
  douyin: "抖音",
  kuaishou: "快手",
  youtube: "YouTube",
  twitch: "Twitch",
  local: "本地联调",
};

const eventNames: Record<string, string> = {
  "message.created": "弹幕",
  "gift.received": "礼物",
  "super_chat.received": "醒目留言",
  "membership.received": "上舰",
  "song.requested": "点歌",
  "speech.reply": "角色回复",
  "song.performance": "演唱记录",
  "live.system": "系统事件",
  "live.metrics": "指标采样",
};

const statusNames: Record<string, string> = {
  active: "有效",
  completed: "已完成",
  received: "已接收",
  generating: "生成中",
  generated: "已生成",
  queued: "已入队",
  executing: "执行中",
  started: "已开始",
  failed: "失败",
  cancelled: "已取消",
  interrupted: "已中断",
  pending_confirmation: "待确认",
  awaiting_submission: "待提交",
  rejected: "已拒绝",
  expired: "已过期",
  revoked: "已撤销",
  merged: "已合并",
};

function providerName(value: string): string {
  return providerNames[value.toLowerCase()] ?? value;
}

function formatDateTime(value: string | null | undefined): string {
  return value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "—";
}

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours) return `${hours}小时${minutes}分`;
  return `${minutes}分${seconds % 60}秒`;
}

function formatMoney(amount: number | null | undefined, currency = "CNY"): string {
  if (amount === null || amount === undefined) return "—";
  const formatted = amount.toLocaleString("zh-CN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency === "CNY" ? `¥${formatted}` : `${currency} ${formatted}`;
}

function formatMoneyTotals(values: Array<{ currency: string; amount: number }>): string {
  return values.length
    ? values.map((value) => formatMoney(value.amount, value.currency)).join(" · ")
    : "¥0.00";
}

function eventCount(row: SessionRow, type: string): number {
  return row.eventTypeCounts[type] ?? 0;
}

function eventIdentityLabel(row: EventRow): string {
  if (row.eventType === "speech.reply") return row.character.displayName;
  if (row.eventType === "song.performance") return row.songTitle ? `《${row.songTitle}》` : "—";
  return row.actorName ?? "—";
}

function statusTag(status: string): React.ReactNode {
  const color = ["failed", "rejected", "revoked"].includes(status)
    ? "red"
    : ["active", "completed"].includes(status)
      ? "green"
      : ["pending_confirmation", "awaiting_submission", "generating", "executing"].includes(status)
        ? "gold"
        : "default";
  return <Tag color={color}>{statusNames[status] ?? status}</Tag>;
}

function sessionStatusTag(session: SessionRow): React.ReactNode {
  return session.durationBasis === "inactivity_timeout"
    ? <Tag color="orange">超时截止</Tag>
    : statusTag(session.status);
}

function copyableId(value: string | null | undefined): React.ReactNode {
  return value
    ? <Typography.Text copyable={{ text: value }} className="live-data-id">{value}</Typography.Text>
    : "—";
}

export function LiveManagementPage() {
  const characterScope = useCharacterScope();
  const [section, setSection] = useState("overview");
  const [selectedEvent, setSelectedEvent] = useState<EventRow | null>(null);
  const overview = useQuery({
    queryKey: ["live-data-overview", characterScope.characterId ?? "all"],
    queryFn: () => api.get<LiveOverview>("/api/v1/admin/channels/live", characterScope),
  });

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }} className="live-data-page">
      <Tabs
        activeKey={section}
        onChange={setSection}
        className="live-data-tabs"
        tabBarExtraContent={(
          <Button icon={<ReloadOutlined />} loading={overview.isFetching} onClick={() => void overview.refetch()}>
            刷新数据
          </Button>
        )}
        items={[
          { key: "overview", label: <span><DatabaseOutlined />数据概览</span>, children: <OverviewSection query={overview} onSection={setSection} /> },
          { key: "sessions", label: <span><HistoryOutlined />场次数据</span>, children: <SessionsSection characterScope={characterScope} /> },
          { key: "events", label: <span><MessageOutlined />互动事件</span>, children: <EventsSection characterScope={characterScope} onSelect={setSelectedEvent} /> },
          { key: "song-requests", label: <span><CustomerServiceOutlined />点歌统计</span>, children: <SongRequestsSection characterScope={characterScope} /> },
          { key: "contacts", label: <span><TeamOutlined />公开用户</span>, children: <ContactsSection characterScope={characterScope} /> },
          { key: "bindings", label: <span><LinkOutlined />身份绑定</span>, children: <BindingsSection characterScope={characterScope} /> },
          { key: "memories", label: <span><ReadOutlined />公开记忆</span>, children: <MemoriesSection characterScope={characterScope} /> },
          { key: "sources", label: <span><CloudServerOutlined />数据源</span>, children: <SourcesSection query={overview} /> },
          { key: "settings", label: <span><SettingOutlined />设置</span>, children: <LiveSettingsSection /> },
        ]}
      />

      <Drawer title="事件数据详情" width={620} open={Boolean(selectedEvent)} onClose={() => setSelectedEvent(null)}>
        {selectedEvent && <EventDetail event={selectedEvent} />}
      </Drawer>
    </Space>
  );
}

function LiveSettingsSection() {
  const { admin } = useAuth();
  const isSuperAdmin = admin?.roles.some((role) => role.key === "super_admin") ?? false;
  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {isSuperAdmin && <LiveRuntimePairingCard />}
      <ChannelServiceControlCard
        queryKey="live-runtime-service-control"
        endpoint="/api/v1/admin/channels/live/service-control"
        title="直播后端服务"
        enabledMessage="当前环境允许 DreamLive 接入并响应直播活动"
        disabledMessage="当前环境不会接收或响应直播活动"
        description="本开关只影响当前部署环境。开启前请确认线上服务器与测试服务器中只有一个环境开启，避免同一场直播被重复响应。"
        requirementLabels={{
          developmentLoopback: "本机联调通道",
          nodeTokenConfigured: "旧版共享节点令牌",
          pairedNodeConfigured: "已配对 DreamLive 电脑",
        }}
      />
      {isSuperAdmin && <LiveTestDataCleanupCard />}
    </Space>
  );
}

function LiveRuntimePairingCard() {
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateReason, setGenerateReason] = useState("");
  const [issuedCode, setIssuedCode] = useState<LiveRuntimePairingCodeResult | null>(null);
  const [revokingNode, setRevokingNode] = useState<RuntimeNodeAccessRecord | null>(null);
  const [revokeConfirmation, setRevokeConfirmation] = useState("");
  const [revokeReason, setRevokeReason] = useState("");

  const pairing = useQuery({
    queryKey: ["live-runtime-pairing-code"],
    queryFn: () => api.get<LiveRuntimePairingStatus>("/api/v1/admin/channels/live/pairing-code"),
    refetchInterval: 5_000,
  });
  const nodes = useQuery({
    queryKey: ["live-runtime-access-nodes"],
    queryFn: () => api.get<RuntimeNodeAccessRecord[]>("/api/v1/admin/channels/live/runtime-nodes"),
    refetchInterval: 5_000,
  });

  const generate = useMutation({
    mutationFn: () => api.post<LiveRuntimePairingCodeResult>(
      "/api/v1/admin/channels/live/pairing-code",
      { reason: generateReason.trim() },
    ),
    onSuccess: async (result) => {
      setIssuedCode(result);
      setGenerateOpen(false);
      setGenerateReason("");
      await pairing.refetch();
      message.success("配对码已生成，只能使用一次");
    },
    onError: (error) => message.error((error as Error).message),
  });
  const revoke = useMutation({
    mutationFn: (node: RuntimeNodeAccessRecord) => api.patch<RuntimeNodeAccessRecord[]>(
      `/api/v1/admin/channels/live/runtime-nodes/${encodeURIComponent(node.id)}/revoke`,
      { confirmation: revokeConfirmation.trim(), reason: revokeReason.trim() },
    ),
    onSuccess: async (result) => {
      queryClient.setQueryData(["live-runtime-access-nodes"], result);
      setRevokingNode(null);
      setRevokeConfirmation("");
      setRevokeReason("");
      await queryClient.invalidateQueries({ queryKey: ["live-runtime-service-control"] });
      message.success("这台 DreamLive 电脑的访问权限已撤销");
    },
    onError: (error) => message.error((error as Error).message),
  });

  const activeNodeSignature = nodes.data
    ?.filter((node) => node.status === "active" && node.hasCredential)
    .map((node) => `${node.id}:${node.updatedAt}`)
    .join("|") ?? "";
  useEffect(() => {
    if (nodes.data) {
      void queryClient.invalidateQueries({ queryKey: ["live-runtime-service-control"] });
    }
  }, [activeNodeSignature, nodes.data, queryClient]);

  const columns: ColumnsType<RuntimeNodeAccessRecord> = [
    {
      title: "电脑",
      key: "device",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{row.deviceName ?? "未命名电脑"}</Typography.Text>
          <Typography.Text type="secondary" copyable={{ text: row.nodeKey }}>
            {row.nodeKey}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: "凭据",
      key: "credential",
      width: 180,
      render: (_, row) => row.hasCredential ? (
        <Space direction="vertical" size={0}>
          <Tag color="success">已安全配对</Tag>
          <Typography.Text type="secondary">指纹 {row.fingerprint ?? "—"}</Typography.Text>
        </Space>
      ) : <Tag>无有效凭据</Tag>,
    },
    {
      title: "状态",
      dataIndex: "status",
      width: 110,
      render: (value: string, row) => row.revokedAt || value === "archived"
        ? <Tag color="red">已撤销</Tag>
        : <Tag color="green">可连接</Tag>,
    },
    {
      title: "最近连接",
      dataIndex: "lastSeenAt",
      width: 180,
      render: (value: string | null) => formatDateTime(value),
    },
    {
      title: "场次数",
      dataIndex: "sessionCount",
      width: 90,
    },
    {
      title: "操作",
      key: "actions",
      width: 100,
      render: (_, row) => (
        <Button
          danger
          size="small"
          disabled={Boolean(row.revokedAt) || row.status === "archived" || !row.hasCredential}
          onClick={() => {
            setRevokingNode(row);
            setRevokeConfirmation("");
            setRevokeReason("");
          }}
        >
          撤销
        </Button>
      ),
    },
  ];

  const pairingActive = pairing.data?.active ?? false;
  const displayedCodeActive = issuedCode
    ? dayjs(issuedCode.expiresAt).isAfter(dayjs()) && pairingActive
    : false;
  const canGenerate = generateReason.trim().length >= 4;
  const canRevoke = Boolean(
    revokingNode
    && revokeConfirmation.trim() === revokingNode.nodeKey
    && revokeReason.trim().length >= 4,
  );

  return (
    <>
      <Card
        title={<Space><KeyOutlined />DreamLive 电脑配对</Space>}
        extra={<Tag color="red">仅超级管理员</Tag>}
        loading={pairing.isLoading || nodes.isLoading}
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Alert
            type="info"
            showIcon
            message="每台直播电脑只需配对一次"
            description="先在这里生成一次性配对码，再到 DreamLive 的 Dreamverse 连接设置中输入。配对码 10 分钟内有效且只能使用一次；长期凭据由 DreamLive 加密保存在该电脑的 Windows 账户中。"
          />

          {issuedCode && displayedCodeActive ? (
            <Card size="small">
              <Flex justify="space-between" align="center" gap={16} wrap>
                <Space direction="vertical" size={2}>
                  <Typography.Text type="secondary">本次配对码</Typography.Text>
                  <Typography.Text
                    copyable={{ text: issuedCode.pairingCode.replaceAll("-", "") }}
                    style={{ fontSize: 26, letterSpacing: 3, fontWeight: 700 }}
                  >
                    {issuedCode.pairingCode}
                  </Typography.Text>
                  <Typography.Text type="secondary">
                    {formatDateTime(issuedCode.expiresAt)} 失效
                  </Typography.Text>
                </Space>
                <Tag color="processing">等待 DreamLive 使用</Tag>
              </Flex>
            </Card>
          ) : pairingActive ? (
            <Alert
              type="warning"
              showIcon
              message={`已有配对码在 ${formatDateTime(pairing.data?.expiresAt)} 前有效`}
              description="出于安全原因，离开本页后不会再次显示原码。如果没有保存，请重新生成；新码会立即替换旧码。"
            />
          ) : null}

          <Flex justify="space-between" align="center" gap={12} wrap>
            <Typography.Text type="secondary">
              生成新码会使尚未使用的旧码立即失效。
            </Typography.Text>
            <Button type="primary" icon={<KeyOutlined />} onClick={() => setGenerateOpen(true)}>
              {pairingActive ? "重新生成配对码" : "生成配对码"}
            </Button>
          </Flex>

          <Table<RuntimeNodeAccessRecord>
            rowKey="id"
            size="small"
            columns={columns}
            dataSource={nodes.data ?? []}
            pagination={false}
            locale={{ emptyText: <Empty description="还没有配对过 DreamLive 电脑" /> }}
            scroll={{ x: 900 }}
          />
        </Space>
      </Card>

      <Modal
        title="生成 DreamLive 配对码"
        open={generateOpen}
        okText="生成一次性配对码"
        cancelText="取消"
        okButtonProps={{ disabled: !canGenerate }}
        confirmLoading={generate.isPending}
        onCancel={() => {
          if (!generate.isPending) {
            setGenerateOpen(false);
            setGenerateReason("");
          }
        }}
        onOk={() => generate.mutate()}
      >
        <Alert
          type="warning"
          showIcon
          message="配对码有效期为 10 分钟，只能成功使用一次"
          description="只把配对码输入到你正在配置的 DreamLive 电脑，不要通过公开聊天发送。"
          style={{ marginBottom: 16 }}
        />
        <Typography.Text strong>生成原因</Typography.Text>
        <Input.TextArea
          value={generateReason}
          onChange={(event) => setGenerateReason(event.target.value)}
          placeholder="例如：配对直播间主机"
          rows={3}
          maxLength={200}
          showCount
          style={{ marginTop: 8 }}
        />
      </Modal>

      <Modal
        title="撤销 DreamLive 电脑"
        open={Boolean(revokingNode)}
        okText="立即撤销"
        cancelText="取消"
        okButtonProps={{ danger: true, disabled: !canRevoke }}
        confirmLoading={revoke.isPending}
        onCancel={() => {
          if (!revoke.isPending) setRevokingNode(null);
        }}
        onOk={() => revokingNode && revoke.mutate(revokingNode)}
      >
        <Alert
          type="error"
          showIcon
          message="撤销后，这台电脑会立即失去访问权限"
          description="如需恢复，需要在管理后台生成新码并重新配对。历史场次数据不会被删除。"
          style={{ marginBottom: 16 }}
        />
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <label>
            <Typography.Text strong>输入完整节点键确认</Typography.Text>
            <Input
              value={revokeConfirmation}
              onChange={(event) => setRevokeConfirmation(event.target.value)}
              placeholder={revokingNode?.nodeKey}
              style={{ marginTop: 6 }}
            />
          </label>
          <label>
            <Typography.Text strong>撤销原因</Typography.Text>
            <Input.TextArea
              value={revokeReason}
              onChange={(event) => setRevokeReason(event.target.value)}
              placeholder="例如：这台电脑已不再用于直播"
              rows={3}
              maxLength={200}
              showCount
              style={{ marginTop: 6 }}
            />
          </label>
        </Space>
      </Modal>
    </>
  );
}

function LiveTestDataCleanupCard() {
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [reason, setReason] = useState("");
  const clear = useMutation({
    mutationFn: () => api.del<LiveTestDataClearResult>("/api/v1/admin/channels/live/test-data", {
      confirmation,
      reason: reason.trim(),
    }),
    onSuccess: async (result) => {
      message.success(`已清理 ${result.sessions} 个测试场次、${result.events} 条事件和 ${result.contacts} 个测试联系人`);
      setOpen(false);
      setConfirmation("");
      setReason("");
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === "string"
            && (key.includes("/api/v1/admin/channels/live") || key.startsWith("live-"));
        },
      });
    },
    onError: (error) => message.error((error as Error).message),
  });
  const confirmed = confirmation === "CLEAR_LIVE_TEST_DATA" && reason.trim().length >= 2;

  const close = () => {
    if (clear.isPending) return;
    setOpen(false);
    setConfirmation("");
    setReason("");
  };

  return (
    <>
      <Card
        title="测试数据清理"
        extra={<Tag color="red">仅超级管理员</Tag>}
        className="live-data-danger-card"
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Alert
            type="warning"
            showIcon
            message="永久删除所有直播测试场次及其关联数据"
            description="会删除 mode=test 的场次、事件、测试联系人、相遇、场次记忆及无其他引用的平台身份。正式数据、身份绑定、共享身份和 Brain / LLM 成本审计会保留。"
          />
          <div>
            <Button danger icon={<DeleteOutlined />} onClick={() => setOpen(true)}>
              一键清理测试数据
            </Button>
          </div>
        </Space>
      </Card>

      <Modal
        title="清理全部直播测试数据"
        open={open}
        okText="永久清理"
        cancelText="取消"
        okButtonProps={{ danger: true, disabled: !confirmed }}
        confirmLoading={clear.isPending}
        onCancel={close}
        onOk={() => clear.mutate()}
        destroyOnClose
      >
        <Alert
          type="error"
          showIcon
          message="此操作不可恢复"
          description="只会清理测试模式数据，但被删除的测试场次、事件和测试联系人无法找回。"
        />
        <Space direction="vertical" size={12} style={{ width: "100%", marginTop: 16 }}>
          <label>
            <Typography.Text strong>输入 CLEAR_LIVE_TEST_DATA 确认</Typography.Text>
            <Input
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              style={{ marginTop: 6 }}
            />
          </label>
          <label>
            <Typography.Text strong>操作理由</Typography.Text>
            <Input
              value={reason}
              maxLength={200}
              placeholder="至少 2 个字，将写入审计日志"
              onChange={(event) => setReason(event.target.value)}
              style={{ marginTop: 6 }}
            />
          </label>
        </Space>
      </Modal>
    </>
  );
}

function OverviewSection({
  query,
  onSection,
}: {
  query: ReturnType<typeof useQuery<LiveOverview>>;
  onSection: (value: string) => void;
}) {
  if (query.error) return <Alert type="error" showIcon message={(query.error as Error).message} />;
  if (!query.data) return <Card loading title="直播数据概览" />;
  const data = query.data;
  const memoryCoverage = data.integrity.memoryEligibleSessions
    ? data.integrity.sessionsWithMemory / data.integrity.memoryEligibleSessions * 100
    : 0;
  const encounterCoverage = data.totals.contacts
    ? data.integrity.contactsWithEncounter / data.totals.contacts * 100
    : 0;

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Row gutter={[12, 12]}>
        <Col xs={12} md={8} xl={4}><DataMetric icon={<HistoryOutlined />} title="场次" value={data.totals.sessions} detail={distributionText(data.distributions.sessionsByMode)} /></Col>
        <Col xs={12} md={8} xl={4}><DataMetric icon={<MessageOutlined />} title="互动事件" value={data.totals.events} detail={distributionText(data.distributions.eventsByDirection)} /></Col>
        <Col xs={12} md={8} xl={4}><DataMetric icon={<UserOutlined />} title="平台 Actor" value={data.totals.actors} detail="观察到的公开身份" /></Col>
        <Col xs={12} md={8} xl={4}><DataMetric icon={<TeamOutlined />} title="公开联系人" value={data.totals.contacts} detail={`${data.integrity.contactsBoundToUser} 个已绑定站内用户`} /></Col>
        <Col xs={12} md={8} xl={4}><DataMetric icon={<LinkOutlined />} title="身份绑定" value={data.totals.bindings} detail={distributionText(data.distributions.bindingsByStatus)} /></Col>
        <Col xs={12} md={8} xl={4}><DataMetric icon={<ReadOutlined />} title="公开记忆" value={data.totals.endpointMemories + data.totals.encounters} detail={`${data.totals.endpointMemories} 场次 · ${data.totals.encounters} 相遇`} /></Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={15}>
          <Card title="最近场次" extra={<Button type="link" onClick={() => onSection("sessions")}>查看全部</Button>}>
            {data.recentSessions.length ? (
              <Table<SessionRow>
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={data.recentSessions}
                columns={overviewSessionColumns}
              />
            ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无场次数据" />}
          </Card>
        </Col>
        <Col xs={24} xl={9}>
          <Card title="数据完整性" className="live-data-integrity">
            <IntegrityRow
              title="场次记忆覆盖"
              detail={`${data.integrity.sessionsWithMemory} / ${data.integrity.memoryEligibleSessions} 个已结束正式场次有 Endpoint Memory`}
              percent={memoryCoverage}
            />
            <IntegrityRow
              title="联系人相遇摘要"
              detail={`${data.integrity.contactsWithEncounter} / ${data.totals.contacts} 个联系人有 Encounter`}
              percent={encounterCoverage}
            />
            <DividerRow />
            <DataCheck label="缺少摘要的正式场次" value={data.integrity.sessionsWithoutMemory} warn={data.integrity.sessionsWithoutMemory > 0} />
            <DataCheck label="已配置直播间" value={data.totals.endpoints} />
            <DataCheck label="接入节点记录" value={data.totals.nodes} />
          </Card>
        </Col>
      </Row>

      <Card title="状态分布" extra={<Typography.Text type="secondary">统计于 {formatDateTime(data.generatedAt)}</Typography.Text>}>
        <Row gutter={[24, 16]}>
          <DistributionBlock title="场次状态" rows={data.distributions.sessionsByStatus} />
          <DistributionBlock title="场次模式" rows={data.distributions.sessionsByMode} />
          <DistributionBlock title="事件方向" rows={data.distributions.eventsByDirection} />
          <DistributionBlock title="联系人状态" rows={data.distributions.contactsByStatus} />
          <DistributionBlock title="绑定状态" rows={data.distributions.bindingsByStatus} />
        </Row>
      </Card>
    </Space>
  );
}

function DataMetric({ icon, title, value, detail }: { icon: React.ReactNode; title: string; value: number; detail: string }) {
  return (
    <Card className="live-data-metric" styles={{ body: { padding: 15 } }}>
      <span className="live-data-metric__icon">{icon}</span>
      <Statistic title={title} value={value} />
      <Typography.Text type="secondary" ellipsis title={detail}>{detail || "暂无分布数据"}</Typography.Text>
    </Card>
  );
}

function distributionText(rows: Distribution[]): string {
  return rows.map((row) => `${statusNames[row.key] ?? row.key} ${row.count}`).join(" · ");
}

function IntegrityRow({ title, detail, percent }: { title: string; detail: string; percent: number }) {
  return (
    <div className="live-data-integrity__row">
      <Flex justify="space-between"><strong>{title}</strong><span>{percent.toFixed(1)}%</span></Flex>
      <Progress percent={Number(percent.toFixed(1))} strokeColor="#ad4a57" size="small" />
      <Typography.Text type="secondary">{detail}</Typography.Text>
    </div>
  );
}

function DividerRow() {
  return <div className="live-data-divider" />;
}

function DataCheck({ label, value, warn = false }: { label: string; value: number; warn?: boolean }) {
  return (
    <div className="live-data-check">
      <span className={warn ? "is-warn" : "is-ok"}>{warn ? <WarningOutlined /> : <CheckCircleOutlined />}</span>
      <Typography.Text>{label}</Typography.Text>
      <strong>{value.toLocaleString()}</strong>
    </div>
  );
}

function DistributionBlock({ title, rows }: { title: string; rows: Distribution[] }) {
  return (
    <Col xs={24} sm={12} lg={8} xl={5}>
      <Typography.Text strong>{title}</Typography.Text>
      <Flex gap={6} wrap style={{ marginTop: 9 }}>
        {rows.length ? rows.map((row) => <Tag key={row.key}>{statusNames[row.key] ?? row.key} · {row.count}</Tag>) : <Typography.Text type="secondary">暂无数据</Typography.Text>}
      </Flex>
    </Col>
  );
}

const overviewSessionColumns: ColumnsType<SessionRow> = [
  { title: "开始时间", dataIndex: "startedAt", width: 156, render: formatDateTime },
  { title: "直播间", dataIndex: "roomName", ellipsis: true },
  { title: "状态", key: "status", width: 90, render: (_, row) => sessionStatusTag(row) },
  { title: "时长", dataIndex: "durationSeconds", width: 96, render: formatDuration },
  { title: "总金额", dataIndex: "amountsByCurrency", width: 120, align: "right", render: formatMoneyTotals },
  { title: "事件", dataIndex: "eventCount", width: 72, align: "right" },
];

const sessionColumns: ColumnsType<SessionRow> = [
  { title: "开始时间", dataIndex: "startedAt", width: 156, render: formatDateTime },
  { title: "平台", dataIndex: "providerKey", width: 100, render: providerName },
  { title: "直播间", dataIndex: "roomName", width: 150, ellipsis: true },
  { title: "模式", dataIndex: "mode", width: 76, render: (value: string) => <Tag color={value === "production" ? "purple" : "gold"}>{value === "production" ? "正式" : "测试"}</Tag> },
  { title: "状态", key: "status", width: 112, render: (_, row) => <Space size={4}>{statusTag(row.status)}{row.durationBasis === "inactivity_timeout" && <Tag color="orange">超时截止</Tag>}</Space> },
  { title: "时长", dataIndex: "durationSeconds", width: 96, render: formatDuration },
  { title: "总金额", dataIndex: "amountsByCurrency", width: 120, align: "right", render: formatMoneyTotals },
  { title: "事件", dataIndex: "eventCount", width: 72, align: "right" },
  { title: "弹幕", key: "chatCount", width: 66, align: "right", render: (_, row) => eventCount(row, "message.created") },
  { title: "SC", key: "superChatCount", width: 60, align: "right", render: (_, row) => eventCount(row, "super_chat.received") },
  { title: "礼物", key: "giftCount", width: 66, align: "right", render: (_, row) => eventCount(row, "gift.received") },
  { title: "上舰", key: "membershipCount", width: 66, align: "right", render: (_, row) => eventCount(row, "membership.received") },
  { title: "点歌", key: "songRequestCount", width: 66, align: "right", render: (_, row) => eventCount(row, "song.requested") },
  { title: "角色回复", key: "replyCount", width: 82, align: "right", render: (_, row) => eventCount(row, "speech.reply") },
  { title: "演唱", key: "songCount", width: 66, align: "right", render: (_, row) => eventCount(row, "song.performance") },
  { title: "相遇", dataIndex: "encounterCount", width: 72, align: "right" },
  { title: "记忆", dataIndex: "hasEndpointMemory", width: 70, render: (value: boolean) => value ? <Tag color="green">有</Tag> : <Tag>无</Tag> },
  { title: "最后请求", dataIndex: "lastRequestAt", width: 156, render: formatDateTime },
];

function SessionsSection({ characterScope }: { characterScope: { characterId?: string } }) {
  const filterKeys = ["status", "mode", "endpointId"];
  const { params, setParam, setParams } = useTableParams(filterKeys);
  return (
    <DataTable<SessionRow>
      path="/api/v1/admin/channels/live/sessions"
      fixedParams={characterScope}
      filterKeys={filterKeys}
      title="场次数据"
      extra={<Typography.Text type="secondary">每条 ChannelSession 对应一段公开渠道场次记录</Typography.Text>}
      columns={sessionColumns}
      toolbar={
        <FilterBar onReset={() => setParams({ status: null, mode: null, endpointId: null })}>
          <Select allowClear placeholder="场次状态" value={params.status as string | undefined} onChange={(value) => setParam("status", value)} style={{ width: 130 }} options={["active", "completed", "failed"].map((value) => ({ value, label: statusNames[value] ?? value }))} />
          <Select allowClear placeholder="场次模式" value={params.mode as string | undefined} onChange={(value) => setParam("mode", value)} style={{ width: 130 }} options={[{ value: "production", label: "正式" }, { value: "test", label: "测试" }]} />
          <UuidFilter placeholder="完整 Endpoint ID" value={params.endpointId as string | undefined} onApply={(value) => setParam("endpointId", value)} width={240} />
        </FilterBar>
      }
      expandable={{ expandedRowRender: (row) => <SessionDetail session={row} /> }}
    />
  );
}

function SessionDetail({ session }: { session: SessionRow }) {
  return (
    <Descriptions bordered size="small" column={2} className="live-data-details">
      <Descriptions.Item label="场次 ID">{copyableId(session.id)}</Descriptions.Item>
      <Descriptions.Item label="客户端场次 ID">{copyableId(session.clientSessionId)}</Descriptions.Item>
      <Descriptions.Item label="平台场次 ID">{copyableId(session.externalSessionId)}</Descriptions.Item>
      <Descriptions.Item label="Endpoint ID">{copyableId(session.endpointId)}</Descriptions.Item>
      <Descriptions.Item label="运行节点">{session.runtimeNode?.nodeKey ?? "—"}</Descriptions.Item>
      <Descriptions.Item label="角色">{session.character.displayName}</Descriptions.Item>
      <Descriptions.Item label="直播间名称">{session.roomName}</Descriptions.Item>
      <Descriptions.Item label="场次总金额">{formatMoneyTotals(session.amountsByCurrency)}</Descriptions.Item>
      <Descriptions.Item label="数据库状态">{statusTag(session.status)}</Descriptions.Item>
      <Descriptions.Item label="最后请求">{formatDateTime(session.lastRequestAt)}</Descriptions.Item>
      <Descriptions.Item label="计时截止">{formatDateTime(session.effectiveEndedAt)} · {session.durationBasis === "reported" ? "正常结束" : session.durationBasis === "inactivity_timeout" ? "10 分钟无请求" : "仍在活动"}</Descriptions.Item>
      <Descriptions.Item label="确认事件水位">{session.acknowledgedThroughSequence}</Descriptions.Item>
      <Descriptions.Item label="房间生成水位">{session.roomGeneratedThroughSequence}</Descriptions.Item>
      <Descriptions.Item label="事件类型" span={2}>
        <Flex gap={4} wrap>
          {Object.keys(session.eventTypeCounts).length
            ? Object.entries(session.eventTypeCounts).map(([type, count]) => (
                <Tag key={type}>{eventNames[type] ?? type} · {count}</Tag>
              ))
            : <Typography.Text type="secondary">暂无事件</Typography.Text>}
        </Flex>
      </Descriptions.Item>
      <Descriptions.Item label="场次说明" span={2}>{session.roomIntroduction ?? "—"}</Descriptions.Item>
      <Descriptions.Item label="指标快照" span={2}><pre className="live-data-json">{JSON.stringify(session.metrics, null, 2)}</pre></Descriptions.Item>
    </Descriptions>
  );
}

function SongRequestsSection({ characterScope }: { characterScope: { characterId?: string } }) {
  const filterKeys = ["outcome", "includeTest", "search", "songSort", "songOrder"];
  const { params, setParam, setParams } = useTableParams(filterKeys);
  const [search, setSearch] = useState(String(params.search ?? ""));
  const [selectedSong, setSelectedSong] = useState<SongRequestRow | null>(null);
  const [requesterPage, setRequesterPage] = useState(1);
  const requesterPageSize = 20;
  const requesters = useQuery({
    queryKey: [
      "live-song-requesters",
      characterScope.characterId ?? "all",
      selectedSong?.songKey,
      params.outcome ?? "all",
      params.includeTest ?? "false",
      requesterPage,
    ],
    queryFn: () => api.get<Paged<SongRequesterRow>>(
      "/api/v1/admin/channels/live/song-requesters",
      {
        ...characterScope,
        songKey: selectedSong!.songKey,
        ...(params.outcome ? { outcome: params.outcome } : {}),
        includeTest: params.includeTest === "true" ? "true" : "false",
        page: requesterPage,
        pageSize: requesterPageSize,
      },
    ),
    enabled: Boolean(selectedSong),
  });

  const columns: ColumnsType<SongRequestRow> = [
    {
      title: "歌曲",
      dataIndex: "songTitle",
      render: (value: string) => <Typography.Text strong>《{value}》</Typography.Text>,
    },
    { title: "总次数", dataIndex: "totalCount", key: "count", width: 92, align: "right", sorter: true },
    { title: "点歌人数", dataIndex: "requesterCount", width: 92, align: "right" },
    { title: "成功", dataIndex: "successCount", width: 78, align: "right", render: (value: number) => <Typography.Text type="success">{value}</Typography.Text> },
    { title: "失败", dataIndex: "failedCount", width: 78, align: "right", render: (value: number) => value ? <Typography.Text type="danger">{value}</Typography.Text> : 0 },
    { title: "重复", dataIndex: "duplicateCount", width: 78, align: "right", render: (value: number) => value ? <Tag color="gold">{value}</Tag> : 0 },
    { title: "待确认", dataIndex: "pendingCount", width: 82, align: "right", render: (value: number) => value || "—" },
    { title: "最近点歌人", dataIndex: "latestRequesterName", width: 140, ellipsis: true },
    { title: "最近点歌", dataIndex: "latestRequestedAt", key: "latest", width: 156, sorter: true, render: formatDateTime },
  ];

  return (
    <>
      <DataTable<SongRequestRow>
        path="/api/v1/admin/channels/live/song-requests"
        fixedParams={characterScope}
        filterKeys={filterKeys}
        columns={columns}
        rowKey="songKey"
        serverSort={{ default: "count", defaultOrder: "desc" }}
        sortQueryKeys={{ sort: "songSort", order: "songOrder" }}
        onRow={(row) => ({
          onClick: () => {
            setRequesterPage(1);
            setSelectedSong(row);
          },
          style: { cursor: "pointer" },
        })}
        toolbar={(
          <FilterBar onReset={() => {
            setSearch("");
            setParams({ outcome: null, includeTest: null, search: null, songSort: "count", songOrder: "desc" });
          }}>
            <Select
              allowClear
              placeholder="成功或失败"
              value={params.outcome as string | undefined}
              onChange={(value) => setParam("outcome", value)}
              style={{ width: 132 }}
              options={[
                { value: "success", label: "成功" },
                { value: "failed", label: "失败" },
              ]}
            />
            <Space size={6}>
              <Switch
                size="small"
                checked={params.includeTest === "true"}
                onChange={(checked) => setParam("includeTest", checked ? "true" : null)}
              />
              <Typography.Text>包含测试数据</Typography.Text>
            </Space>
            <Select
              value={(params.songSort as string | undefined) ?? "count"}
              onChange={(value) => setParams({ songSort: value, songOrder: "desc" })}
              style={{ width: 150 }}
              options={[
                { value: "count", label: "按总次数排序" },
                { value: "latest", label: "按最新点歌排序" },
              ]}
            />
            <Input.Search
              placeholder="搜索歌曲"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onSearch={(value) => setParam("search", value)}
              allowClear
              style={{ width: 220 }}
            />
            <Typography.Text type="secondary">成功表示已进入歌单；重复点歌仍逐次计数。点击歌曲查看点歌人明细</Typography.Text>
          </FilterBar>
        )}
      />

      <Modal
        title={selectedSong ? `《${selectedSong.songTitle}》点歌明细` : "点歌明细"}
        open={Boolean(selectedSong)}
        onCancel={() => setSelectedSong(null)}
        footer={null}
        width={820}
        destroyOnHidden
      >
        {selectedSong && (
          <Space direction="vertical" size={14} style={{ width: "100%" }}>
            <Flex gap={8} wrap>
              <Tag color="blue">共 {selectedSong.totalCount} 次</Tag>
              <Tag color="green">成功 {selectedSong.successCount}</Tag>
              <Tag color="red">失败 {selectedSong.failedCount}</Tag>
              <Tag color="gold">重复 {selectedSong.duplicateCount}</Tag>
              {selectedSong.pendingCount > 0 && <Tag>待确认 {selectedSong.pendingCount}</Tag>}
            </Flex>
            {requesters.error ? (
              <Alert type="error" showIcon message={(requesters.error as Error).message} />
            ) : (
              <Table<SongRequesterRow>
                size="small"
                rowKey="requesterKey"
                loading={requesters.isFetching}
                dataSource={requesters.data?.items ?? []}
                locale={{ emptyText: "没有符合条件的点歌人" }}
                columns={[
                  { title: "点歌人", dataIndex: "requesterName" },
                  { title: "总次数", dataIndex: "totalCount", width: 82, align: "right" },
                  { title: "成功", dataIndex: "successCount", width: 72, align: "right" },
                  { title: "失败", dataIndex: "failedCount", width: 72, align: "right" },
                  { title: "重复", dataIndex: "duplicateCount", width: 72, align: "right" },
                  { title: "最近点歌", dataIndex: "latestRequestedAt", width: 156, render: formatDateTime },
                ]}
                pagination={{
                  current: requesterPage,
                  pageSize: requesterPageSize,
                  total: requesters.data?.total ?? 0,
                  showSizeChanger: false,
                  showTotal: (total) => `共 ${total} 人`,
                  onChange: setRequesterPage,
                }}
              />
            )}
          </Space>
        )}
      </Modal>
    </>
  );
}

function EventsSection({ characterScope, onSelect }: { characterScope: { characterId?: string }; onSelect: (row: EventRow) => void }) {
  const filterKeys = ["direction", "eventType", "search"];
  const { params, setParam, setParams } = useTableParams(filterKeys);
  const [search, setSearch] = useState(String(params.search ?? ""));
  const [singleSession, setSingleSession] = useState(true);
  const [selectedSessionId, setSelectedSessionId] = useState<string>();
  const [eventStatus, setEventStatus] = useState<string | undefined>();
  const sessions = useQuery({
    queryKey: ["live-event-session-options", characterScope],
    queryFn: () => api.get<Paged<SessionRow>>("/api/v1/admin/channels/live/sessions", {
      ...characterScope,
      page: 1,
      pageSize: 200,
    }),
  });
  const sessionOptions = sessions.data?.items ?? [];

  useEffect(() => {
    if (!singleSession) return;
    const latestSessionId = sessionOptions[0]?.id;
    if (!latestSessionId) {
      if (selectedSessionId) setSelectedSessionId(undefined);
      return;
    }
    if (!selectedSessionId || !sessionOptions.some((session) => session.id === selectedSessionId)) {
      setSelectedSessionId(latestSessionId);
    }
  }, [selectedSessionId, sessionOptions, singleSession]);

  const columns: ColumnsType<EventRow> = [
    { title: "发生时间", key: "occurredAt", dataIndex: "occurredAt", width: 156, sorter: true, render: formatDateTime },
    { title: "方向", dataIndex: "direction", width: 76, render: (value: string) => <Tag color={value === "inbound" ? "blue" : value === "outbound" ? "purple" : "default"}>{value === "inbound" ? "入站" : value === "outbound" ? "出站" : "系统"}</Tag> },
    { title: "类型", dataIndex: "eventType", width: 118, render: (value: string) => eventNames[value] ?? value },
    { title: "金额", key: "amount", dataIndex: "amount", width: 110, align: "right", sorter: true, render: (_, row) => formatMoney(row.amount, row.currency ?? "CNY") },
    { title: "公开身份", key: "identity", width: 128, ellipsis: true, render: (_, row) => eventIdentityLabel(row) },
    {
      title: "内容预览",
      key: "text",
      render: (_, row) => (
        <div onClick={(event) => event.stopPropagation()}>
          <Typography.Paragraph
            className="live-data-event-preview"
            ellipsis={{
              rows: 2,
              expandable: "collapsible",
              symbol: (expanded) => expanded ? "收起" : "展开",
            }}
          >
            {row.executedTextPreview ?? row.textPreview ?? "—"}
          </Typography.Paragraph>
        </div>
      ),
    },
    { title: "状态", dataIndex: "status", width: 88, render: statusTag },
    { title: "序号", dataIndex: "sourceSequence", width: 72, align: "right", render: (value: number | null) => value ?? "—" },
    { title: "场次", dataIndex: "sessionId", width: 92, render: (value: string | null) => value ? <Typography.Text className="live-data-short-id">{value.slice(0, 8)}</Typography.Text> : "—" },
  ];

  const toolbar = (
    <FilterBar onReset={() => { setSearch(""); setEventStatus(undefined); setParams({ direction: null, eventType: null, search: null }); }}>
      <Switch
        checked={singleSession}
        checkedChildren="单场"
        unCheckedChildren="全部数据"
        onChange={setSingleSession}
      />
      {singleSession && (
        <Select
          showSearch
          optionFilterProp="label"
          loading={sessions.isLoading}
          placeholder="选择场次"
          value={selectedSessionId}
          onChange={setSelectedSessionId}
          style={{ width: 188 }}
          options={sessionOptions.map((session) => ({
            value: session.id,
            label: formatDateTime(session.startedAt),
          }))}
        />
      )}
      <Select allowClear placeholder="方向" value={params.direction as string | undefined} onChange={(value) => setParam("direction", value)} style={{ width: 110 }} options={[{ value: "inbound", label: "入站" }, { value: "outbound", label: "出站" }, { value: "system", label: "系统" }]} />
      <Select allowClear placeholder="事件类型" value={params.eventType as string | undefined} onChange={(value) => setParam("eventType", value)} style={{ width: 148 }} options={Object.entries(eventNames).map(([value, label]) => ({ value, label }))} />
      <Select allowClear placeholder="状态" value={eventStatus} onChange={setEventStatus} style={{ width: 126 }} options={["received", "generated", "completed", "failed", "cancelled", "interrupted"].map((value) => ({ value, label: statusNames[value] ?? value }))} />
      <Input.Search placeholder="搜索昵称或内容" value={search} onChange={(event) => setSearch(event.target.value)} onSearch={(value) => setParam("search", value)} allowClear style={{ width: 220 }} />
    </FilterBar>
  );

  if (singleSession && !selectedSessionId) {
    return (
      <Card styles={{ body: { padding: 12 } }}>
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {toolbar}
          {sessions.error
            ? <Alert type="error" showIcon message={(sessions.error as Error).message} />
            : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={sessions.isLoading ? "正在加载场次" : "暂无场次"} />}
        </Space>
      </Card>
    );
  }

  return (
    <DataTable<EventRow>
      path="/api/v1/admin/channels/live/events"
      fixedParams={{
        ...characterScope,
        ...(singleSession ? { sessionId: selectedSessionId } : {}),
        ...(eventStatus ? { status: eventStatus } : {}),
      }}
      filterKeys={filterKeys}
      columns={columns}
      serverSort={{ default: "occurredAt", defaultOrder: "desc" }}
      onRow={(row) => ({ onClick: () => onSelect(row), style: { cursor: "pointer" } })}
      toolbar={toolbar}
      tableLayout="fixed"
      disableHorizontalScroll
    />
  );
}

function EventDetail({ event }: { event: EventRow }) {
  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Space wrap>{statusTag(event.status)}<Tag>{eventNames[event.eventType] ?? event.eventType}</Tag><Tag>{event.direction}</Tag></Space>
      <Descriptions bordered size="small" column={1}>
        <Descriptions.Item label="事件 ID">{copyableId(event.id)}</Descriptions.Item>
        <Descriptions.Item label="场次 ID">{copyableId(event.sessionId)}</Descriptions.Item>
        <Descriptions.Item label="Endpoint">{event.endpointTitle} · {copyableId(event.endpointId)}</Descriptions.Item>
        <Descriptions.Item label="场内序号">{event.sourceSequence ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="公开身份">{event.actorName ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="金额">{formatMoney(event.amount, event.currency ?? "CNY")}</Descriptions.Item>
        <Descriptions.Item label="目标联系人">{event.targetContact ? `${event.targetContact.preferredName ?? "未命名"} · ${event.targetContact.id}` : "—"}</Descriptions.Item>
        <Descriptions.Item label="来源 / 回复类型">{event.origin ?? "—"} / {event.replyKind ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="BrainRun">{copyableId(event.brainRunId)}</Descriptions.Item>
        <Descriptions.Item label="运行任务">{copyableId(event.runtimeTaskId)}{event.runtimeTaskRevision ? ` · r${event.runtimeTaskRevision}` : ""}</Descriptions.Item>
      </Descriptions>
      <Card size="small" title="入站 / 生成正文"><Typography.Paragraph className="mono">{event.text ?? "—"}</Typography.Paragraph></Card>
      <Card size="small" title="实际执行正文"><Typography.Paragraph className="mono">{event.executedText ?? "—"}</Typography.Paragraph></Card>
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="发生">{formatDateTime(event.occurredAt)}</Descriptions.Item>
        <Descriptions.Item label="生成">{formatDateTime(event.generatedAt)}</Descriptions.Item>
        <Descriptions.Item label="开始">{formatDateTime(event.startedAt)}</Descriptions.Item>
        <Descriptions.Item label="终态">{formatDateTime(event.executedAt)}</Descriptions.Item>
        <Descriptions.Item label="来源事件数">{event.sourceEventCount}</Descriptions.Item>
        <Descriptions.Item label="覆盖确认">{formatDateTime(event.roomReplyCoveredAt)}</Descriptions.Item>
      </Descriptions>
    </Space>
  );
}

function ContactsSection({ characterScope }: { characterScope: { characterId?: string } }) {
  const filterKeys = ["status", "scope", "bound", "search"];
  const { params, setParam, setParams } = useTableParams(filterKeys);
  const [search, setSearch] = useState(String(params.search ?? ""));
  const columns: ColumnsType<ContactRow> = [
    { title: "公开联系人", key: "name", width: 170, render: (_, row) => <Space direction="vertical" size={0}><Typography.Text strong>{row.preferredName ?? row.actors[0]?.displayName ?? "未命名公开联系人"}</Typography.Text><Typography.Text type="secondary" className="live-data-short-id">{row.id.slice(0, 12)}</Typography.Text></Space> },
    { title: "角色", key: "character", width: 100, render: (_, row) => row.character.displayName },
    { title: "平台身份", dataIndex: "actorLinkCount", width: 92, align: "right" },
    { title: "有效相遇", dataIndex: "meaningfulEncounterCount", width: 92, align: "right" },
    { title: "摘要", dataIndex: "encounterCount", width: 72, align: "right" },
    { title: "定向事件", dataIndex: "targetedEventCount", width: 86, align: "right" },
    { title: "站内绑定", dataIndex: "isUserBound", width: 94, render: (value: boolean) => value ? <Tag color="green">已绑定</Tag> : <Tag>未绑定</Tag> },
    { title: "状态", dataIndex: "status", width: 86, render: statusTag },
    { title: "最近出现", dataIndex: "lastEncounteredAt", width: 156, render: formatDateTime },
  ];
  return (
    <DataTable<ContactRow>
      path="/api/v1/admin/channels/live/contacts"
      fixedParams={characterScope}
      filterKeys={filterKeys}
      title="公开用户"
      extra={<Typography.Text type="secondary">Contact 是角色在公开渠道可再次识别的人，不等于站内 User</Typography.Text>}
      columns={columns}
      toolbar={
        <FilterBar onReset={() => { setSearch(""); setParams({ status: null, scope: null, bound: null, search: null }); }}>
          <Select allowClear placeholder="状态" value={params.status as string | undefined} onChange={(value) => setParam("status", value)} style={{ width: 120 }} options={["active", "merged", "revoked"].map((value) => ({ value, label: statusNames[value] ?? value }))} />
          <Select allowClear placeholder="数据作用域" value={params.scope as string | undefined} onChange={(value) => setParam("scope", value)} style={{ width: 132 }} options={[{ value: "persistent", label: "长期" }, { value: "session_test", label: "测试场临时" }]} />
          <Select allowClear placeholder="站内绑定" value={params.bound as string | undefined} onChange={(value) => setParam("bound", value)} style={{ width: 126 }} options={[{ value: "yes", label: "已绑定" }, { value: "no", label: "未绑定" }]} />
          <Input.Search placeholder="搜索公开昵称" value={search} onChange={(event) => setSearch(event.target.value)} onSearch={(value) => setParam("search", value)} allowClear style={{ width: 220 }} />
        </FilterBar>
      }
      expandable={{ expandedRowRender: (row) => <ContactActors contact={row} /> }}
    />
  );
}

function ContactActors({ contact }: { contact: ContactRow }) {
  return (
    <Space direction="vertical" size={10} style={{ width: "100%" }}>
      <Descriptions size="small" column={3}>
        <Descriptions.Item label="Contact ID">{copyableId(contact.id)}</Descriptions.Item>
        <Descriptions.Item label="站内 User ID">{copyableId(contact.userId)}</Descriptions.Item>
        <Descriptions.Item label="记忆版本">v{contact.memoryVersion}</Descriptions.Item>
        <Descriptions.Item label="首次出现">{formatDateTime(contact.firstEncounteredAt)}</Descriptions.Item>
        <Descriptions.Item label="最近出现">{formatDateTime(contact.lastEncounteredAt)}</Descriptions.Item>
        <Descriptions.Item label="作用域">{contact.scope}</Descriptions.Item>
      </Descriptions>
      <Table<ContactActor>
        size="small"
        rowKey="linkId"
        pagination={false}
        dataSource={contact.actors}
        locale={{ emptyText: "没有有效的平台身份关联" }}
        columns={[
          { title: "平台", dataIndex: "providerKey", width: 100, render: providerName },
          { title: "公开昵称", dataIndex: "displayName", render: (value) => value ?? "—" },
          { title: "平台身份", dataIndex: "externalActorIdMasked", width: 160, render: (value) => value ?? "—" },
          { title: "关联依据", dataIndex: "linkMethod", width: 110 },
          { title: "置信度", dataIndex: "confidence", width: 80, render: (value: number | null) => value === null ? "—" : value.toFixed(2) },
          { title: "最近出现", dataIndex: "lastSeenAt", width: 156, render: formatDateTime },
        ]}
      />
    </Space>
  );
}

function BindingsSection({ characterScope }: { characterScope: { characterId?: string } }) {
  const filterKeys = ["status", "endpointId", "search"];
  const { params, setParam, setParams } = useTableParams(filterKeys);
  const [search, setSearch] = useState(String(params.search ?? ""));
  const columns: ColumnsType<BindingRow> = [
    { title: "创建时间", dataIndex: "createdAt", width: 156, render: formatDateTime },
    { title: "公开昵称", dataIndex: "actorDisplayName", width: 140, render: (value) => value ?? "—" },
    { title: "平台", dataIndex: "providerKey", width: 100, render: providerName },
    { title: "直播间", key: "endpoint", ellipsis: true, render: (_, row) => row.endpoint?.title ?? "不限 Endpoint" },
    { title: "平台身份", dataIndex: "externalActorIdMasked", width: 150, render: (value) => value ?? "尚未提交" },
    { title: "User ID", dataIndex: "userId", width: 120, render: (value: string) => <Typography.Text className="live-data-short-id" copyable={{ text: value }}>{value.slice(0, 12)}</Typography.Text> },
    { title: "状态", dataIndex: "status", width: 96, render: statusTag },
    { title: "确认时间", dataIndex: "confirmedAt", width: 156, render: formatDateTime },
  ];
  return (
    <DataTable<BindingRow>
      path="/api/v1/admin/channels/live/bindings"
      fixedParams={characterScope}
      filterKeys={filterKeys}
      title="身份绑定"
      extra={<Typography.Text type="secondary">只展示绑定事实；平台原始身份已脱敏</Typography.Text>}
      columns={columns}
      toolbar={
        <FilterBar onReset={() => { setSearch(""); setParams({ status: null, endpointId: null, search: null }); }}>
          <Select allowClear placeholder="绑定状态" value={params.status as string | undefined} onChange={(value) => setParam("status", value)} style={{ width: 140 }} options={["awaiting_submission", "pending_confirmation", "active", "rejected", "expired", "revoked"].map((value) => ({ value, label: statusNames[value] ?? value }))} />
          <UuidFilter placeholder="完整 Endpoint ID" value={params.endpointId as string | undefined} onApply={(value) => setParam("endpointId", value)} />
          <Input.Search placeholder="公开昵称或完整 User ID" value={search} onChange={(event) => setSearch(event.target.value)} onSearch={(value) => setParam("search", value)} allowClear style={{ width: 250 }} />
        </FilterBar>
      }
      expandable={{ expandedRowRender: (row) => (
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="绑定 ID">{copyableId(row.id)}</Descriptions.Item>
          <Descriptions.Item label="User ID">{copyableId(row.userId)}</Descriptions.Item>
          <Descriptions.Item label="提交时间">{formatDateTime(row.submittedAt)}</Descriptions.Item>
          <Descriptions.Item label="生效时间">{formatDateTime(row.effectiveFrom)}</Descriptions.Item>
          <Descriptions.Item label="拒绝时间">{formatDateTime(row.rejectedAt)}</Descriptions.Item>
          <Descriptions.Item label="撤销时间">{formatDateTime(row.revokedAt)}</Descriptions.Item>
          <Descriptions.Item label="历史身份键">{row.historicalActorKeyCount} 个不可逆摘要</Descriptions.Item>
          <Descriptions.Item label="过期时间">{formatDateTime(row.expiresAt)}</Descriptions.Item>
        </Descriptions>
      ) }}
    />
  );
}

function MemoriesSection({ characterScope }: { characterScope: { characterId?: string } }) {
  const filterKeys = ["kind", "sessionId", "endpointId", "search"];
  const { params, setParam, setParams } = useTableParams(filterKeys);
  const kind = params.kind === "endpoint" ? "endpoint" : "contact";
  const [search, setSearch] = useState(String(params.search ?? ""));
  const columns = useMemo<ColumnsType<MemoryRow>>(() => [
    { title: "结束时间", dataIndex: "endedAt", width: 156, render: formatDateTime },
    { title: kind === "contact" ? "直播用户" : "直播间", key: "subject", width: 160, ellipsis: true, render: (_, row) => kind === "contact" ? row.contactName : row.endpointTitle },
    ...(kind === "contact" ? [{
      title: "分析状态",
      key: "analysisStatus",
      width: 96,
      render: (_: unknown, row: MemoryRow) => row.brainRunId
        ? <Tag color="green">已分析</Tag>
        : (row.analysisVersion ?? 0) > 0
          ? <Tag>未达门槛</Tag>
          : <Tag color="gold">待分析</Tag>,
    }] : []),
    { title: "摘要", dataIndex: "summary", ellipsis: true },
    { title: "主题", dataIndex: "topics", width: 180, render: (topics: string[]) => <Flex gap={4} wrap>{topics.slice(0, 3).map((topic) => <Tag key={topic}>{topic}</Tag>)}</Flex> },
    { title: "来源事件", dataIndex: "sourceEventCount", width: 86, align: "right" },
    { title: kind === "contact" ? "完成回复" : "现场动作", key: "actions", width: 86, align: "right", render: (_, row) => kind === "contact" ? (row.completedReplyCount ?? 0) : (row.sourceActionCount ?? 0) },
    { title: "显著度", dataIndex: "salience", width: 76, align: "right" },
  ], [kind]);
  return (
    <DataTable<MemoryRow>
      path="/api/v1/admin/channels/live/memories"
      fixedParams={{ ...characterScope, kind }}
      filterKeys={filterKeys}
      title={kind === "contact" ? "用户直播记忆" : "直播间场次记忆"}
      extra={<Typography.Text type="secondary">每场总结；不使用向量，不含任何私人关系记忆</Typography.Text>}
      columns={columns}
      toolbar={
        <FilterBar onReset={() => { setSearch(""); setParams({ sessionId: null, endpointId: null, search: null }); }}>
          <Select value={kind} onChange={(value) => setParam("kind", value)} style={{ width: 170 }} options={[{ value: "contact", label: "用户直播记忆" }, { value: "endpoint", label: "直播间场次记忆" }]} />
          <UuidFilter placeholder="完整场次 ID" value={params.sessionId as string | undefined} onApply={(value) => setParam("sessionId", value)} />
          <UuidFilter placeholder="完整 Endpoint ID" value={params.endpointId as string | undefined} onApply={(value) => setParam("endpointId", value)} />
          <Input.Search placeholder="搜索摘要正文" value={search} onChange={(event) => setSearch(event.target.value)} onSearch={(value) => setParam("search", value)} allowClear style={{ width: 220 }} />
        </FilterBar>
      }
      expandable={{ expandedRowRender: (row) => (
        <div className="live-data-memory-detail">
          <Typography.Paragraph>{row.summary}</Typography.Paragraph>
          <Descriptions size="small" column={3}>
            <Descriptions.Item label="记忆 ID">{copyableId(row.id)}</Descriptions.Item>
            <Descriptions.Item label="场次 ID">{copyableId(row.sessionId)}</Descriptions.Item>
            <Descriptions.Item label="Endpoint ID">{copyableId(row.endpointId)}</Descriptions.Item>
            {row.contactId && <Descriptions.Item label="Contact ID">{copyableId(row.contactId)}</Descriptions.Item>}
            {row.brainRunId && <Descriptions.Item label="BrainRun">{copyableId(row.brainRunId)}</Descriptions.Item>}
            {row.kind === "contact" && <Descriptions.Item label="分析版本">v{row.analysisVersion ?? 0}</Descriptions.Item>}
            {row.preferredNameEvidenceEventId && <Descriptions.Item label="称呼证据 Event">{copyableId(row.preferredNameEvidenceEventId)}</Descriptions.Item>}
            <Descriptions.Item label="创建时间">{formatDateTime(row.createdAt)}</Descriptions.Item>
          </Descriptions>
        </div>
      ) }}
    />
  );
}

function SourcesSection({ query }: { query: ReturnType<typeof useQuery<LiveOverview>> }) {
  if (query.error) return <Alert type="error" showIcon message={(query.error as Error).message} />;
  if (!query.data) return <Card loading title="数据源" />;
  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card title="直播间 Endpoint" extra={<Typography.Text type="secondary">平台账号与可寻址直播间的数据库记录</Typography.Text>}>
        <Table<EndpointRecord>
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={query.data.endpoints}
          locale={{ emptyText: "尚未配置 live_room Endpoint" }}
          columns={[
            { title: "平台", key: "provider", width: 100, render: (_, row) => providerName(row.account.providerKey) },
            { title: "账号", key: "account", width: 160, render: (_, row) => row.account.displayName ?? row.account.key },
            { title: "直播间", dataIndex: "displayName" },
            { title: "平台 ID", dataIndex: "externalEndpointIdMasked", width: 150, render: (value) => value ?? "—" },
            { title: "已配置", dataIndex: "configuredAt", width: 90, render: (value) => value ? <Tag color="green">是</Tag> : <Tag>否</Tag> },
            { title: "启用", dataIndex: "enabled", width: 74, render: (value) => value ? <Tag color="green">是</Tag> : <Tag>否</Tag> },
            { title: "场次", dataIndex: "sessionCount", width: 70, align: "right" },
            { title: "事件", dataIndex: "eventCount", width: 70, align: "right" },
            { title: "记忆", dataIndex: "memoryCount", width: 70, align: "right" },
            { title: "最后事件", dataIndex: "lastEventAt", width: 156, render: formatDateTime },
          ]}
          expandable={{ expandedRowRender: (row) => (
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Endpoint ID">{copyableId(row.id)}</Descriptions.Item>
              <Descriptions.Item label="账号 ID">{copyableId(row.account.id)}</Descriptions.Item>
              <Descriptions.Item label="角色">{row.character.displayName}</Descriptions.Item>
              <Descriptions.Item label="账号状态">{statusTag(row.account.status)}</Descriptions.Item>
              <Descriptions.Item label="能力检查">{formatDateTime(row.account.capabilitiesCheckedAt)}</Descriptions.Item>
              <Descriptions.Item label="更新时间">{formatDateTime(row.updatedAt)}</Descriptions.Item>
              <Descriptions.Item label="账号能力" span={2}><pre className="live-data-json">{JSON.stringify(row.account.capabilities, null, 2)}</pre></Descriptions.Item>
            </Descriptions>
          ) }}
        />
      </Card>

      <Card title="接入节点" extra={<Typography.Text type="secondary">仅展示节点登记与最近同步记录，不提供运行控制</Typography.Text>}>
        <Table<NodeRecord>
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={query.data.nodes}
          locale={{ emptyText: "尚无接入节点记录" }}
          columns={[
            { title: "节点键", dataIndex: "nodeKey" },
            { title: "状态", dataIndex: "status", width: 100, render: statusTag },
            { title: "累计场次", dataIndex: "sessionCount", width: 100, align: "right" },
            { title: "最近同步", dataIndex: "lastSeenAt", width: 156, render: formatDateTime },
            { title: "撤销时间", dataIndex: "revokedAt", width: 156, render: formatDateTime },
            { title: "更新时间", dataIndex: "updatedAt", width: 156, render: formatDateTime },
          ]}
          expandable={{ expandedRowRender: (row) => (
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="节点 ID">{copyableId(row.id)}</Descriptions.Item>
              <Descriptions.Item label="公钥指纹">{row.publicKeyFingerprint ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="能力" span={2}><pre className="live-data-json">{JSON.stringify(row.capabilities, null, 2)}</pre></Descriptions.Item>
            </Descriptions>
          ) }}
        />
      </Card>
    </Space>
  );
}

function FilterBar({ children, onReset }: { children: React.ReactNode; onReset: () => void }) {
  return (
    <Flex gap={8} wrap align="center" className="live-data-filter">
      {children}
      <Button onClick={onReset}>清空筛选</Button>
    </Flex>
  );
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function UuidFilter({
  value,
  placeholder,
  onApply,
  width = 220,
}: {
  value?: string;
  placeholder: string;
  onApply: (value: string | undefined) => void;
  width?: number;
}) {
  const [draft, setDraft] = useState(value ?? "");
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setDraft(value ?? "");
    setInvalid(false);
  }, [value]);

  const apply = (raw: string) => {
    const next = raw.trim();
    if (next && !UUID_PATTERN.test(next)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    onApply(next || undefined);
  };

  return (
    <Input.Search
      allowClear
      placeholder={placeholder}
      value={draft}
      status={invalid ? "error" : undefined}
      title={invalid ? "请输入完整 UUID" : undefined}
      onChange={(event) => {
        const next = event.target.value;
        setDraft(next);
        setInvalid(false);
        if (!next) onApply(undefined);
      }}
      onSearch={apply}
      style={{ width }}
    />
  );
}
